import {Form, Select} from 'antd';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {toast} from 'react-toastify';
import styles from './orderBookTrigger.module.scss';
import {OrderFilter} from './OrderFilter';
import {FieldGroup, ModalShell} from './ui/ModalShell';
import theme from '../ordersTheme.module.scss';
import {BULK_ORDER_OPERATIONS} from 'graphql/mutations/stocks';
import {useMutation, useQuery} from '@apollo/client';
import {brokerOptionLabel} from '../brokerVenues';
import {GET_ALL_BROKERS} from 'graphql/queries/stocks';
import {useEffect} from 'react';

export const OrderBookTrigger = ({
  form,
  close,
  triggerType,
  goBack,
  onKeyCopied,
}) => {
  const [bulkOrderOperation, {loading}] = useMutation(BULK_ORDER_OPERATIONS, {
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
  });
  const {data, loading: brokerLoading} = useQuery(GET_ALL_BROKERS, {});

  useEffect(() => {
    form.setFieldsValue({
      action: triggerType,
    });
  }, [triggerType]);

  const handleSubmit = values => {
    const {action, ...formattedValues} = values;
    bulkOrderOperation({
      variables: {
        input: {
          status: triggerType || action,
          filter: formattedValues,
        },
      },
    })
      .then(({data: {bulkOrderOperation}}) => {
        if (bulkOrderOperation.__typename === 'Error') {
          toast.error(bulkOrderOperation.message);
        } else {
          if (bulkOrderOperation?.data?.key) {
            const copiedKey = bulkOrderOperation?.data?.key;
            navigator.clipboard.writeText(copiedKey);
            onKeyCopied?.(copiedKey, getTitle());
            toast.warn('Order Book Key Copied to Clipboard Successfully');
          }
          toast.success(bulkOrderOperation.message);
          close();
          form.resetFields();
        }
      })
      .catch(error => toast.error(error.message));
  };

  const getTitle = () => {
    if (triggerType === 'OPEN') return 'Open Orders Trigger';
    if (triggerType === 'PENDING') return 'Pend Orders Trigger';
    if (triggerType === 'RETRY') return 'Retry Orders Trigger';
    if (triggerType === 'REJECTED') return 'Rejected Orders Trigger';
    if (triggerType === 'EXPIRED') return 'Expire Orders Trigger';
    return 'Order Book Trigger';
  };

  return (
    <Form
      form={form}
      onFinish={handleSubmit}
      layout="vertical"
      className={styles.form}>
      <ModalShell
        title={getTitle()}
        subtitle="Runs the order book operation over every order matching the filter below. The action is fixed by the trigger you chose."
        onBack={goBack}
        backLabel="Auto triggers"
        footer={<SubmitButton disabled={loading} label="Trigger order book" />}>
        <FieldGroup title="Operation">
          <Form.Item
            className={theme.fieldFull}
            initialValue={triggerType}
            name="action"
            label="Action">
            <Select
              allowClear
              defaultValue={triggerType}
              disabled={!!triggerType}>
              {getStatusOptions(triggerType)}
            </Select>
          </Form.Item>
          <Form.Item name="brokerId" label="Broker">
            <Select loading={brokerLoading}>
              {data?.getBrokers?.data.map(item => (
                <Select.Option key={item.id} value={item.id}>
                  {brokerOptionLabel(item)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="brokerFormat" label="Broker Format">
            <Select allowClear loading={brokerLoading}>
              {data?.getBrokers?.data.map(item => (
                <Select.Option key={item.id} value={item.id}>
                  {brokerOptionLabel(item)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </FieldGroup>
        <FieldGroup
          title="Which orders"
          columns={1}
          description="Leave a field empty to place no restriction on it.">
          <OrderFilter form={form} isExport={true} showStatus={true} />
        </FieldGroup>
      </ModalShell>
    </Form>
  );
};

const getStatusOptions = triggerType => {
  switch (triggerType) {
    case 'OPEN':
      return [
        <Select.Option key="OPEN" value="OPEN">
          Open
        </Select.Option>,
      ];
    case 'RETRY':
      return [
        <Select.Option key="RETRY" value="RETRY">
          Retry
        </Select.Option>,
      ];
    case 'REJECTED':
      return [
        <Select.Option key="REJECTED" value="REJECTED">
          Rejected
        </Select.Option>,
      ];
    case 'EXPIRED':
      return [
        <Select.Option key="EXPIRED" value="EXPIRED">
          Expired
        </Select.Option>,
      ];
    case 'PENDING':
      return [
        <Select.Option key="PENDING" value="PENDING">
          Pending
        </Select.Option>,
      ];
    default:
      return [
        <Select.Option key="RETRY" value="RETRY">
          Retry
        </Select.Option>,
        <Select.Option key="REJECTED" value="REJECTED">
          Rejected
        </Select.Option>,
        <Select.Option key="EXPIRED" value="EXPIRED">
          Expired
        </Select.Option>,
        <Select.Option key="PENDING" value="PENDING">
          Pending
        </Select.Option>,
      ];
  }
};
